import React, { useRef, useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { MAPBOX_TOKEN } from '../../../config'

interface ZonePolygonMapProps {
  /** GeoJSON polygon or multipolygon geometry for the zone */
  geoPolygon: { type: string; coordinates: number[][][] | number[][][][] }
  /** GeoJSON polygon for the land parcel */
  landGeoPolygon?: { type: string; coordinates: number[][][] | number[][][][] }
  /** WGS84 center point for initial view */
  geoCenter?: { lat: number; lon: number }
}

const addPolygonToMap = (
  map: mapboxgl.Map,
  id: string,
  geometry: { type: string; coordinates: any },
  fillColor: string,
  fillOpacity: number,
  lineColor: string,
  lineWidth: number,
  lineDash?: number[]
) => {
  const geojson: GeoJSON.Feature = {
    type: 'Feature',
    properties: {},
    geometry: {
      type: geometry.type as 'Polygon' | 'MultiPolygon',
      coordinates: geometry.coordinates,
    },
  }

  map.addSource(id, { type: 'geojson', data: geojson })

  map.addLayer({
    id: `${id}-fill`,
    type: 'fill',
    source: id,
    paint: {
      'fill-color': fillColor,
      'fill-opacity': fillOpacity,
    },
  })

  const linePaint: mapboxgl.LinePaint = {
    'line-color': lineColor,
    'line-width': lineWidth,
  }

  map.addLayer({
    id: `${id}-outline`,
    type: 'line',
    source: id,
    paint: linePaint,
    ...(lineDash ? { layout: { 'line-cap': 'round' as const } } : {}),
  })

  if (lineDash) {
    map.setPaintProperty(`${id}-outline`, 'line-dasharray', lineDash)
  }
}

const getPolygonBounds = (geometry: {
  type: string
  coordinates: number[][][] | number[][][][]
}): mapboxgl.LngLatBounds => {
  const bounds = new mapboxgl.LngLatBounds()
  const coords =
    geometry.type.toLowerCase() === 'multipolygon'
      ? (geometry.coordinates as number[][][][]).flat()
      : (geometry.coordinates as number[][][])

  coords.forEach((ring) => {
    ring.forEach((coord) => {
      bounds.extend([coord[0], coord[1]])
    })
  })

  return bounds
}

/**
 * Simple map showing a zone polygon outline and optionally a land parcel.
 * Used for ALG estimate justification — shows the zone boundary and land
 * without WMS layers (unlike the GEO source map).
 */
export const ZonePolygonMap: React.FC<ZonePolygonMapProps> = ({
  geoPolygon,
  landGeoPolygon,
  geoCenter,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)

  useEffect(() => {
    if (!containerRef.current || !geoPolygon?.coordinates) return

    mapboxgl.accessToken = MAPBOX_TOKEN

    const center: [number, number] = geoCenter
      ? [geoCenter.lon, geoCenter.lat]
      : [6.63, 46.52]

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center,
      zoom: 15,
      attributionControl: false,
    })

    map.addControl(
      new mapboxgl.NavigationControl({ showCompass: false }),
      'top-right'
    )

    map.on('load', () => {
      // Zone polygon — popety blue, dashed outline
      addPolygonToMap(
        map,
        'zone',
        geoPolygon,
        '#00ADE6',
        0.1,
        '#00ADE6',
        2,
        [2, 1]
      )

      // Land parcel — darker solid outline on top
      if (landGeoPolygon?.coordinates) {
        addPolygonToMap(
          map,
          'land',
          landGeoPolygon,
          '#00ADE6',
          0.2,
          '#0088BB',
          2.5
        )
      }

      // Fit to zone bounds
      try {
        const bounds = getPolygonBounds(geoPolygon)
        map.fitBounds(bounds, { padding: 30, maxZoom: 17 })
      } catch {
        // Fallback: stay at initial center/zoom
      }
    })

    mapRef.current = map

    return () => {
      map.remove()
      mapRef.current = null
    }
  }, [geoPolygon, landGeoPolygon, geoCenter])

  return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
}
